import { useEffect, useState, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatError } from "@/api";
import { toast } from "sonner";
import { Share2, Copy, QrCode, ExternalLink, MessageCircle, Printer, Building2 } from "lucide-react";

const SITE_ORIGIN = window.location.origin;

/**
 * Hospital / Clinic admin's "Our Public Profile Link & QR Panel".
 * Shows the /hospital/{slug} URL + QR + one-click printable A4 front-desk poster.
 */
export default function HospitalPublicProfileCard() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const posterRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/me/public-profile");
        setInfo(data);
      } catch (err) {
        toast.error(formatError(err.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;
  if (!info) return null;
  const link = `${SITE_ORIGIN}${info.path}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(
      `Live queue + instant token booking at ${info.org_name}: ${link}`
    )}`, "_blank");
  };

  const downloadQR = () => {
    const canvas = document.querySelector("[data-testid=hospital-qr] canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `upchaar-${info.slug}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("QR downloaded");
  };

  const printPoster = () => {
    // Reveal the offscreen poster to the print pipeline via a body class
    document.body.classList.add("printing-poster");
    setTimeout(() => {
      window.print();
      document.body.classList.remove("printing-poster");
    }, 100);
  };

  return (
    <>
      {/* Scoped print stylesheet: hides everything except the poster during print */}
      <style>{`
        @media print {
          body.printing-poster > *:not(.upchaar-poster-shell) { display: none !important; }
          .upchaar-poster-shell { display: block !important; position: fixed; inset: 0;
                                    background: white; padding: 20mm; z-index: 9999; }
          @page { size: A4 portrait; margin: 0; }
        }
        .upchaar-poster-shell { display: none; }
      `}</style>

      <Card className="medical-card p-5" data-testid="hospital-public-profile-card">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center shrink-0 shadow-md shadow-sky-500/30">
            <Share2 className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg font-semibold text-slate-900">
              📋 Our Public Profile Link &amp; QR Panel
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Share this link with patients — they land on your directory, see live queues, and book in one tap.
            </p>

            <div className="mt-3 flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-xs text-slate-700"
                 data-testid="hospital-share-link">
              <span className="truncate flex-1">{link}</span>
              <a href={link} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-700 shrink-0">
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={copy} data-testid="hospital-copy-btn" className="gap-1.5">
                <Copy className="w-3.5 h-3.5" /> Copy link
              </Button>
              <Button size="sm" onClick={shareWhatsApp} data-testid="hospital-whatsapp-btn"
                className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowQR((v) => !v)}
                data-testid="hospital-qr-toggle" className="gap-1.5">
                <QrCode className="w-3.5 h-3.5" /> {showQR ? "Hide QR" : "Show QR"}
              </Button>
              <Button size="sm" onClick={printPoster} data-testid="hospital-poster-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                <Printer className="w-3.5 h-3.5" /> 📥 Download Printable Front-Desk Poster (PDF)
              </Button>
            </div>

            {showQR && (
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-white border border-slate-200 rounded-lg" data-testid="hospital-qr">
                <QRCodeCanvas value={link} size={150} level="M" includeMargin={true} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">Front-desk QR</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Print this beside your reception counter. Patients scan → live queue + booking.
                  </p>
                  <Button size="sm" variant="outline" onClick={downloadQR} className="mt-3 gap-1.5" data-testid="hospital-qr-download">
                    <Copy className="w-3.5 h-3.5" /> Download PNG
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Offscreen printable A4 poster */}
      <div ref={posterRef} className="upchaar-poster-shell" data-testid="hospital-poster">
        <div style={{
          border: "3px solid #0ea5e9", borderRadius: 16, padding: "28mm 18mm", textAlign: "center",
          height: "calc(100vh - 40mm)", display: "flex", flexDirection: "column",
          justifyContent: "space-between", background: "linear-gradient(180deg, #f0f9ff 0%, #ecfdf5 100%)",
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", marginBottom: "10mm" }}>
              {info.org_image ? (
                <img src={info.org_image} alt="logo" style={{ width: 72, height: 72, borderRadius: 12, objectFit: "cover" }} />
              ) : (
                <div style={{ width: 72, height: 72, borderRadius: 12, background: "#0ea5e9",
                              display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Building2 style={{ width: 36, height: 36, color: "white" }} />
                </div>
              )}
              <div style={{ textAlign: "left" }}>
                <p style={{ fontSize: 32, fontWeight: 800, color: "#0f172a", margin: 0, lineHeight: 1 }}>{info.org_name}</p>
                {info.city && <p style={{ fontSize: 16, color: "#475569", margin: "6px 0 0" }}>{info.city}</p>}
              </div>
            </div>
            <p style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", margin: "0 0 4mm" }}>
              Scan to Check Live Queue &amp; Book Tokens Instantly!
            </p>
            <p style={{ fontSize: 20, fontWeight: 600, color: "#059669", margin: 0 }}>
              लाइन में खड़े होने से बचें, लाइव टोकन देखने और बुक करने के लिए स्कैन करें!
            </p>
          </div>

          <div style={{ display: "flex", justifyContent: "center", margin: "8mm 0" }}>
            <div style={{ padding: 16, background: "white", borderRadius: 16, boxShadow: "0 6px 20px rgba(2,132,199,0.15)" }}>
              <QRCodeCanvas value={link} size={280} level="H" includeMargin={true} />
            </div>
          </div>

          <div style={{ fontSize: 14, color: "#475569" }}>
            <p style={{ margin: "0 0 2mm" }}>{link}</p>
            <p style={{ margin: 0, fontSize: 12 }}>Powered by <b>UpchaarQ</b> · No app install needed</p>
          </div>
        </div>
      </div>
    </>
  );
}
